"use client";

import { DotList } from "@/components/discover/DotList";
import { InfoItem } from "@/components/discover/InfoItem";
import VerifiedSpondClubBanner from "@/components/discover/VerifiedSpondClubBanner";
import { useClubContactTracking } from "@/hooks/useClubContactTracking";
import { cn } from "@/lib/utils";
import type { ClubSource } from "@/types/discover";
import { createTaxonomyFormatter } from "@/utils/formatTaxonomy";
import { hasClubCallToAction } from "@/utils/clubCallToAction";
import { getClubLogoUrl } from "@/utils/getClubImageUrl";
import { AlertCircle, BadgeCheck, Goal, Trophy, UsersIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import { ClickableLocation } from "./ClickableLocation";
import { ContactFormDialog } from "./ContactFormDialog";
import Cta from "./Cta";
import Share from "./Share";

interface ClubDetailSidebarProps {
  club: ClubSource;
}

export default function ClubDetailSidebar({ club }: ClubDetailSidebarProps) {
  const t = useTranslations();
  const format = createTaxonomyFormatter(t);
  const logoUrl = getClubLogoUrl(club.logo_url, club.id);
  const [showContactForm, setShowContactForm] = useState(false);
  const { trackContactClick } = useClubContactTracking({
    clubId: club.id,
    isSpondClub: club.is_spond_club,
  });
  const hasCta = hasClubCallToAction(club);

  // Pre-format values to check if they have actual content
  const formattedAgeGroup =
    club.age_group.length > 0
      ? club.age_group.map((ag) => format.ageGroup(ag)).join(", ")
      : "";
  const formattedSkillLevel =
    club.skill_level.length > 0
      ? club.skill_level.map((sl) => format.skillLevel(sl)).join(", ")
      : "";

  return (
    <div className="grow rounded-xl sm:p-6 sm:border sm:border-background-secondary bg-background flex flex-col">
      <div className="grid grid-cols-[48px_1fr] gap-x-4 gap-y-1 mb-4">
        <Image
          src={logoUrl}
          alt={`${club.name} logo`}
          width={48}
          height={48}
          className="row-start-1 self-center rounded-full object-cover h-12 w-12 border border-gray-200"
        />
        <h1 className="row-start-1 self-center font-bold text-2xl text-gray-900 leading-tight">
          {club.name}
          {club.is_spond_club && (
            <BadgeCheck
              className="inline ml-1 h-6 w-6 text-white align-middle"
              fill="black"
            />
          )}
        </h1>
        {!!club.club_location && (
          <div className="col-start-2 row-start-2">
            <ClickableLocation location={club.club_location} />
          </div>
        )}
      </div>

      <section className="mt-6 space-y-4">
        {!!club.activity_types?.length && (
          <InfoItem icon={Goal} label={t("clubInfo.activityTypes")}>
            <DotList
              items={club.activity_types.map((type) => format.activity(type))}
            />
          </InfoItem>
        )}

        {!!formattedAgeGroup && (
          <InfoItem icon={UsersIcon} label={t("clubInfo.ageGroup")}>
            <span>{formattedAgeGroup}</span>
          </InfoItem>
        )}

        {!!formattedSkillLevel && (
          <InfoItem icon={Trophy} label={t("clubInfo.skillLevel")}>
            <span>{formattedSkillLevel}</span>
          </InfoItem>
        )}
      </section>

      {club.is_spond_club && (
        <div className="mt-auto pt-4">
          <VerifiedSpondClubBanner />
        </div>
      )}

      {club.is_spond_club && (
        <div className="my-4 hidden sm:flex items-stretch gap-3">
          {hasCta && (
            <Cta
              href={club.signup_url}
              withRegistrationSource
              label={t("signup.ctaClub")}
              className="flex-1"
              ctaKind="club"
              clubId={club.id}
            />
          )}
          <Share
            className={cn(!hasCta && "w-full")}
            title={t("share.club")}
            variant="secondary"
          />
        </div>
      )}

      {!club.is_spond_club && (
        <div className="mt-auto pt-6 space-y-4">
          <div className="flex gap-3 p-4 bg-background-secondary rounded-xl border">
            <AlertCircle className="h-5 w-5 text-foreground-secondary shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              {t("clubInfo.externalDisclaimer")}
            </p>
          </div>
          <div className="flex items-stretch gap-3">
            {hasCta && (
              <Cta
                onClick={() => {
                  trackContactClick();
                  setShowContactForm(true);
                }}
                label={t("contact.contactUs")}
                ctaKind="none"
                className="flex-1 max-sm:hidden"
              />
            )}
            <Share
              className={cn(!hasCta && "w-full", "max-sm:w-full")}
              title={t("share.club")}
              variant="secondary"
            />
          </div>
        </div>
      )}

      {!club.is_spond_club && hasCta && (
        <ContactFormDialog
          open={showContactForm}
          onOpenChange={setShowContactForm}
          clubId={club.id}
          clubName={club.name}
        />
      )}
    </div>
  );
}
